import React from 'react';
import { Sunrise, Sun, Sunset, Download, ExternalLink, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AAWTA } from '@/lib/aawta';

const PREVIEW = [
  {
    label: 'Morning',
    icon: Sunrise,
    text: 'Breakfast, opening keynote and two leadership panels.',
  },
  {
    label: 'Afternoon',
    icon: Sun,
    text: 'Partner spotlight, lunch, breakout sessions and roundtables.',
  },
  {
    label: 'Closing',
    icon: Sunset,
    text: 'Keynote, exhibitor networking and optional VIP reception.',
  },
];

const openExternal = (url) => window.open(url, '_blank', 'noopener,noreferrer');
const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

export const SummitOverview = () => {
  return (
    <section id="summit" className="py-24 md:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto">
          <span className="overline text-aawta-magenta">Summit Overview</span>
          <h2 className="font-display font-bold text-4xl md:text-5xl mt-4 text-aawta-navy tracking-tight leading-[1.1]">
            One Day. Every Sector. One Shared Goal.
          </h2>
          <div className="divider-magenta mt-6 mx-auto" />
          <p className="mt-6 text-[17px] md:text-[18px] leading-relaxed text-aawta-muted">
            Join leaders from education, transportation, logistics, supply chain, workforce
            development, government, and community organizations for an action-focused summit
            designed to strengthen career pathways, connect talent with opportunity, and turn
            collaboration into measurable progress.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {PREVIEW.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={p.label}
                data-testid={`summit-preview-${i + 1}`}
                className="bg-aawta-surface rounded-2xl p-6 md:p-7 border border-aawta-border hover:shadow-aawta-elevated hover:-translate-y-0.5 transition-all"
              >
                <div className="w-11 h-11 rounded-xl bg-aawta-magenta text-white flex items-center justify-center">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="mt-4 overline text-aawta-magenta">{p.label}</div>
                <p className="mt-1 text-aawta-navy text-[15.5px] font-medium leading-relaxed">
                  {p.text}
                </p>
              </div>
            );
          })}
        </div>

        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <Button
            data-testid="summit-view-agenda"
            onClick={() => scrollTo('agenda')}
            variant="outline"
            className="rounded-full border-aawta-navy text-aawta-navy hover:bg-aawta-navy hover:text-white font-medium px-6 py-6"
          >
            View Full Agenda <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button
            data-testid="summit-register-cta"
            onClick={() => openExternal(AAWTA.eventbriteUrl)}
            className="rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white font-medium px-6 py-6 shadow-aawta-magenta hover:-translate-y-0.5 transition-all"
          >
            Register Now
            <ExternalLink className="ml-2 h-4 w-4" />
          </Button>
          <Button
            onClick={() => openExternal(AAWTA.agendaPdfUrl)}
            variant="ghost"
            className="rounded-full text-aawta-navy hover:bg-aawta-surface font-medium px-6 py-6"
          >
            <Download className="mr-2 h-4 w-4" /> Download Agenda PDF
          </Button>
        </div>
      </div>
    </section>
  );
};

export default SummitOverview;
